type UriComponent = Parameters<typeof encodeURIComponent>[0];

/**
 * Build a URI (aka URL) and encode URI components.
 *
 * @example
 * uri`/api/${123}/${'i/have bad,chars'}` ===
 * '/api/123/i%2Fhave%20bad%2Cchars'
 */
export function uri(
	strings: TemplateStringsArray,
	...uriComponents: UriComponent[]
) {
	let output = strings[0] ?? '';

	for (let i = 1; i < strings.length; ++i) {
		output += encodeURIComponent(uriComponents[i - 1]!) + strings[i];
	}

	return output;
}

/**
 * Build a URI relative to a base URI.
 *
 * Note: base must be a valid URL.
 */
uri.from = function uriFrom(base: string | URL) {
	return (...args: Parameters<typeof uri>) =>
		new URL(uri(...args), base).toString();
};

/**
 * Build a query string from an object. Return value includes the leading
 * question mark if there are any query parameters.
 *
 * @example
 * queryString({}) === ''
 *
 * @example
 * queryString({ a: 1, b: undefined, c: 'two' }) === '?a=1&c=two'
 *
 * @example <caption>You can append this to a URI template tag.</caption>
 * uri`/api/${foo}/bars/${barId}` + queryString({ baz })
 */
export function queryString(
	query: Record<string, string | number | undefined>
) {
	const search = new URLSearchParams(
		Object.entries(query)
			.filter(([, value]) => value !== undefined)
			.map(([key, value]) => [key, '' + value])
	).toString();

	return search ? `?${search}` : '';
}
